import { useState, useMemo, useEffect } from 'react';
import { useLinkTo } from '@react-navigation/native';
import { Selection } from '@typeRoots/common/checkListModal';
import { URLS } from 'src/constants/urls';
import {
  useGetApplicationByCompanyIdQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
} from '../../api/applicationApi';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { convertSelection } from './policy.helper';
import { Application } from '@typeRoots/common/application';
import { EndorsementsState } from '../../components/EndorsementsSection';
import { AdditionalInsuredEntry } from '../../components/AdditionalInsuredSection';
import { Address, initialMapAddress } from 'src/types/common/address';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { toISODateLocal, parseYMDToLocalDate } from 'src/helpers/dateHelper';

const boolToYesNo = (val?: boolean | null) => (val === true ? 'Yes' : val === false ? 'No' : '');
const yesNoToBool = (val: string): boolean | null =>
  val === 'Yes' ? true : val === 'No' ? false : null;

export const usePolicy = () => {
  // navigation related
  const linkTo = useLinkTo();
  const application = useSelector((state: RootState) => state.application.application);
  const currentUser = useSelector((state: RootState) => state.users.currentUser);
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const isProducer = accountType === 'producer';

  const isApplicationAlreadyCreated = useSelector(
    (state: RootState) => state.application.applicationCreated,
  );
  const currentCompany = useSelector((state: RootState) => state.company.company);

  // RTK Query Hooks
  //instead of calling network request to get data for application here
  //I will call on first page (lob page) and data from redux
  const { data: fetchedData } = useGetApplicationByCompanyIdQuery(currentCompany?.id || 0);
  const [createApplication] = useCreateApplicationMutation();
  const [updateApplication] = useUpdateApplicationMutation();

  // Owner-related states
  const defaultOwnerName = `${currentUser?.firstName} ${currentUser?.lastName}`;

  const [owner, setOwner] = useState<'yes' | 'no' | null>(
    application?.ownerName === '' || application?.ownerName === defaultOwnerName ? 'yes' : 'no',
  );
  const [ownerName, setOwnerName] = useState<string>(application?.ownerName || defaultOwnerName);
  const [ownerDob, setOwnerDob] = useState<Date | null>(null);

  // DatePicker states
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Operations modal states
  const [isOperationsModalVisible, setIsOperationsModalVisible] = useState(false);
  const [selectedOperations, setSelectedOperations] = useState<string[]>(['Long Haul Trucker']);
  const [annualMileage, setAnnualMileage] = useState<string>(
    application?.annualMileage ? String(application.annualMileage) : '',
  );
  const [selection, setSelection] = useState<Selection[]>([
    {
      type: 'LongHaulTrucker',
      subtype: { name: 'Long Haul Trucker', value: 'long_haul_trucker' },
    },
  ]);

  let selectedCommodities = useMemo(
    () =>
      selection.map(({ type, subtype }) => ({
        type,
        subtype,
      })),
    [selection],
  );

  // Filings-related states — default to null (unanswered/optional)
  const [filingsRequired, setFilingsRequired] = useState<'yes' | 'no' | null>(null);
  const [filings, setFilings] = useState<{ federal: boolean; state: boolean }>({
    federal: false,
    state: false,
  });

  // Additional insured-related states
  const [additionalInsured, setAdditionalInsured] = useState<'yes' | 'no'>('no');
  const [additionalInsuredEntries, setAdditionalInsuredEntries] = useState<
    AdditionalInsuredEntry[]
  >([]);

  // ELD-related states

  // Endorsements-related states
  const [endorsements, setEndorsements] = useState<EndorsementsState>({
    blanketAdditionalInsured: false,
    waiverOfSubrogation: false,
    hiredNonOwnedAuto: false,
    nonOwnedTrailer: false,
  });

  // Ownership / business partners (moved from Growth & Ownership) — producer only
  const [hasOtherOwners, setHasOtherOwners] = useState(boolToYesNo(application?.hasOtherOwners));
  const [otherOwnerRole, setOtherOwnerRole] = useState(application?.otherOwnerRole || '');
  const [otherOwnerWillDrive, setOtherOwnerWillDrive] = useState(
    boolToYesNo(application?.otherOwnerWillDrive),
  );
  const [otherOwnerIsLicensed, setOtherOwnerIsLicensed] = useState(
    boolToYesNo(application?.otherOwnerIsLicensed),
  );

  // Team Driving / Slip Seating (moved from Risk History) — producer only, Operations group
  const [teamDriving, setTeamDriving] = useState(boolToYesNo(application?.teamDriving));

  // Primary Noncontributory (moved from Risk History) — producer only, Endorsements group
  const [pncAuto, setPncAuto] = useState(
    boolToYesNo(application?.primaryNoncontributoryAutoLiabilityRequested),
  );
  const [pncTruckingGl, setPncTruckingGl] = useState(
    boolToYesNo(application?.primaryNoncontributoryTruckingGeneralLiabilityRequested),
  );

  const [policySaveErrorToast, setPolicySaveErrorToast] = useState(false);

  const checkFiliings =
    filingsRequired === null || filingsRequired === 'no' || filings.federal || filings.state;
  const checkAdditionalInsured =
    additionalInsured === 'no' ||
    (additionalInsuredEntries.length > 0 &&
      additionalInsuredEntries.every(
        (entry) => entry.name.length > 0 && entry.address.street?.length > 0,
      ));

  const isNextButtonEnabled =
    ownerName.length > 0 &&
    selectedOperations.length > 0 &&
    checkFiliings &&
    checkAdditionalInsured;

  useEffect(() => {
    trackGTMEvent(
      'policy-questions-page-landed',
      {
        userEvent: 'User landed on Policy Questions screen',
      },
      accountType,
    );
  }, []);

  // Effect to populate local states from application data
  useEffect(() => {
    if (application) {
      setSelectedDate(application.effectiveDateRequested || new Date());

      if (application.operations && application.operations.length > 0) {
        const updatedSelection = convertSelection(application.operations as string[]);
        setSelectedOperations(application.operations);
        setSelection(updatedSelection);
      }

      // Populating filings-related states
      const federalRequested = application.federalAutoLiabilityFilingRequested ?? false;
      const stateRequested = application.stateAutoLiabilityFilingRequested ?? false;
      const isFilingsRequired = federalRequested || stateRequested;

      setFilingsRequired(isFilingsRequired ? 'yes' : 'no');
      setFilings({
        federal: federalRequested,
        state: stateRequested,
      });

      // Populating additional insured-related states
      if (
        application.additionalInsured &&
        application.additionalInsured.name.length > 0 &&
        application.additionalInsured.address.street.length > 0
      ) {
        setAdditionalInsured('yes');
        setAdditionalInsuredEntries([
          {
            name: application.additionalInsured.name,
            address: {
              ...application.additionalInsured.address,
              state:
                application.additionalInsured.address.state?.length > 0
                  ? application.additionalInsured.address.state
                  : 'NY',
              latitude: application.additionalInsured.address.latitude || 0,
              longitude: application.additionalInsured.address.longitude || 0,
            } as Address,
          },
        ]);
      }

      // Populating endorsements-related states
      setEndorsements({
        blanketAdditionalInsured: application.blanketAdditionalInsured ?? false,
        waiverOfSubrogation: application.insuredWaiverSubrogation ?? false,
        hiredNonOwnedAuto: application.hiredNonOwnedAuto ?? false,
        nonOwnedTrailer: application.nonOwnedTrailer ?? false,
      });

      // Populating owner's date of birth state
      if (application.ownerDob) {
        setOwnerDob(parseYMDToLocalDate(application.ownerDob?.toString()) || null);
      } else {
        setOwnerDob(null);
      }

      if (application.annualMileage) {
        setAnnualMileage(String(application.annualMileage));
      }

      // Ownership / business partners (producer)
      setHasOtherOwners(boolToYesNo(application.hasOtherOwners));
      setOtherOwnerRole(application.otherOwnerRole || '');
      setOtherOwnerWillDrive(boolToYesNo(application.otherOwnerWillDrive));
      setOtherOwnerIsLicensed(boolToYesNo(application.otherOwnerIsLicensed));

      // Team driving + Primary Noncontributory (producer)
      setTeamDriving(boolToYesNo(application.teamDriving));
      setPncAuto(boolToYesNo(application.primaryNoncontributoryAutoLiabilityRequested));
      setPncTruckingGl(
        boolToYesNo(application.primaryNoncontributoryTruckingGeneralLiabilityRequested),
      );
    }
  }, [fetchedData, application]);

  const prepareApplicationData = (): Application => {
    // Producer-only fields relocated onto this page (Ownership, Team Driving, Primary
    // Noncontributory). Empty object for customers so their payload is unaffected.
    const producerPolicyFields = isProducer
      ? {
          teamDriving: yesNoToBool(teamDriving),
          hasOtherOwners: yesNoToBool(hasOtherOwners),
          otherOwnerRole: hasOtherOwners === 'Yes' ? otherOwnerRole : undefined,
          otherOwnerWillDrive: hasOtherOwners === 'Yes' ? yesNoToBool(otherOwnerWillDrive) : null,
          otherOwnerIsLicensed:
            hasOtherOwners === 'Yes' && otherOwnerWillDrive === 'No'
              ? yesNoToBool(otherOwnerIsLicensed)
              : null,
          primaryNoncontributoryAutoLiabilityRequested: yesNoToBool(pncAuto),
          primaryNoncontributoryTruckingGeneralLiabilityRequested: yesNoToBool(pncTruckingGl),
        }
      : {};

    return {
      ...(application as Application),
      applicationStep: 'POLICY_QUESTIONS',
      ownerName: ownerName,
      ownerDob: toISODateLocal(ownerDob) as Date | null,
      operations: selectedOperations,
      annualMileage: annualMileage ? Number(annualMileage) : 0,
      federalAutoLiabilityFilingRequested: filings.federal,
      stateAutoLiabilityFilingRequested: filings.state,
      insuredWaiverSubrogation: endorsements.waiverOfSubrogation,
      blanketAdditionalInsured: endorsements.blanketAdditionalInsured,
      hiredNonOwnedAuto: endorsements.hiredNonOwnedAuto,
      nonOwnedTrailer: endorsements.nonOwnedTrailer,
      effectiveDateRequested: selectedDate,
      ...producerPolicyFields,
      // Send first entry as backward-compatible single object
      additionalInsured:
        additionalInsured === 'yes' && additionalInsuredEntries.length > 0
          ? additionalInsuredEntries[0]
          : { name: '', address: initialMapAddress },
    };
  };

  const navigateToAddFreight = () => {
    trackGTMEvent(
      'policy-questions-page-left',
      {
        userEvent: 'User clicked Back on Policy Questions page and navigated to LOB page',
      },
      accountType,
    );

    linkTo(URLS.APPLICATION.INSURANCE);
  };
  const navigateToQuote = () => {
    trackGTMEvent(
      'policy-questions-given',
      {
        userEvent:
          'User filled in policy questions form fields and clicked Next to navigate to losses page',
      },
      accountType,
    );

    handleSaveOrUpdate();
  };
  //handleSaveOrUpdate
  const handleSaveOrUpdate = async () => {
    if (isApplicationAlreadyCreated) {
      try {
        const applicationData = prepareApplicationData();
        await updateApplication({
          id: application?.id as number,
          updatedData: { ...applicationData, company: { id: currentCompany?.id || 0 } },
        }).unwrap();
        linkTo(accountType === 'producer' ? URLS.APPLICATION.RISK_HISTORY : URLS.APPLICATION.LOSS);
        //handleSuccess();
      } catch (err) {
        setPolicySaveErrorToast(true);
        toggleToastState(setPolicySaveErrorToast);
        console.error('Error updating aplication:', err);
      }
    } else {
      try {
        const applicationData = prepareApplicationData();
        await createApplication({
          ...applicationData,
          company: { id: currentCompany?.id || 0 },
        }).unwrap();
        linkTo(URLS.APPLICATION.CONFIRM);
      } catch (err) {
        setPolicySaveErrorToast(true);
        toggleToastState(setPolicySaveErrorToast);
        console.error('Error saving aplication:', err);
      }
    }
  };

  // Grouped return object
  return {
    owner: {
      value: owner,
      set: setOwner,
      name: ownerName,
      setName: setOwnerName,
      dob: ownerDob,
      setDob: setOwnerDob,
    },
    datePicker: {
      selectedDate,
      setSelectedDate,
      isOpen: isDatePickerOpen,
      setOpen: setIsDatePickerOpen,
    },
    operations: {
      isModalVisible: isOperationsModalVisible,
      setModalVisible: setIsOperationsModalVisible,
      selectedOperations,
      setSelectedOperations,
      selection,
      setSelection,
      selectedCommodities,
    },
    annualMileage: {
      value: annualMileage,
      set: setAnnualMileage,
    },
    isProducer,
    ownership: {
      hasOtherOwners,
      setHasOtherOwners,
      role: otherOwnerRole,
      setRole: setOtherOwnerRole,
      willDrive: otherOwnerWillDrive,
      setWillDrive: setOtherOwnerWillDrive,
      isLicensed: otherOwnerIsLicensed,
      setIsLicensed: setOtherOwnerIsLicensed,
    },
    teamDriving: {
      value: teamDriving,
      set: setTeamDriving,
    },
    pnc: {
      auto: pncAuto,
      setAuto: setPncAuto,
      truckingGl: pncTruckingGl,
      setTruckingGl: setPncTruckingGl,
    },
    filings: {
      required: filingsRequired,
      setRequired: setFilingsRequired,
      details: filings,
      setDetails: setFilings,
    },
    endorsements: {
      values: endorsements,
      setValues: setEndorsements,
    },
    additionalInsured: {
      value: additionalInsured,
      set: setAdditionalInsured,
      entries: additionalInsuredEntries,
      setEntries: setAdditionalInsuredEntries,
    },
    screenNavigation: {
      navigateToAddFreight,
      navigateToQuote,
      isNextButtonEnabled,
    },
    policySaveErrorToast,
  };
};
